import React from 'react';
import { ProcessButtons } from './ProcessButtons';
import { StatsCards } from './StatsCards';
import { SearchAndFilter } from './SearchAndFilter';
import { EventCard } from './EventCard';

export const EventListView = ({
  searchTerm,
  setSearchTerm,
  sortBy,
  setSortBy,
  sortedEvents,
  events,
  loading,
  error,
  handleEventClick,
  formatCurrency,
  formatDate,
  onSettlementProcess,
  onReprocess,
  isProcessing,
  summary,  // This comes from parent
  searchKey
}) => {
  // Use summary from API, don't recalculate
  // The summary already contains: TotalEvents, TotalSuccessfulRegistrations, 
  // TotalSuccessAmount, PendingAmount

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-blue-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-20 h-20 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
          </div>
          <p className="mt-6 text-gray-600 text-lg font-medium">Loading events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Failed to load data</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-600 transition-all shadow-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Payment Overview
          </h1>
          <p className="text-gray-600">Monitor and manage all event payments in one place</p>
        </div>
        <ProcessButtons
          onSettlement={onSettlementProcess}
          onReprocess={onReprocess}
          isProcessing={isProcessing}
        />
      </div>

      {/* Stats Cards - Pass the API summary directly */}
      <StatsCards stats={summary} formatCurrency={formatCurrency} />

      {/* Search and Filter */}
      <SearchAndFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sortBy={sortBy}
        setSortBy={setSortBy}
        searchKey={searchKey}
      />

      {/* Events Grid */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">All Events</h2>
        <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-sm font-medium border border-blue-200">
          {sortedEvents.length} event{sortedEvents.length !== 1 ? 's' : ''} found
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        {sortedEvents.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            onEventClick={handleEventClick}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        ))}
      </div>

      {sortedEvents.length === 0 && (
        <div className="text-center py-20">
          <div className="inline-block p-12 bg-white rounded-2xl border border-gray-200 shadow-xl">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-600 mb-4">No events found</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Try adjusting your search terms to find what you're looking for.
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-600 transition-all shadow-lg"
            >
              Clear Search
            </button>
          </div>
        </div>
      )}
    </div>
  );
};